import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { Activity, Task, Response, TodoDetails, User } from '../../types';
import { getNotificationInstance } from '../utils/notification';
import { todosSlice } from './todos';
import { authSlice } from './auth';

const baseQuery = fetchBaseQuery({ baseUrl: '/api/tasks' });

function getErrorDescription(error: unknown): string {
    if (error && typeof error === 'object' && 'data' in error) {
        const data = (error as { data?: { message?: unknown } }).data;
        if (data && typeof data.message === 'string' && data.message && data.message !== 'error') {
            return data.message;
        }
    }
    return 'Произошла ошибка при выполнении запроса';
}

// @ts-ignore
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
    const notification = getNotificationInstance();

    const result = await baseQuery(args, api, extraOptions);
    if (result.error) {
        const description = getErrorDescription(result.error);
        if (notification) {
            notification.error({
                message: 'Ошибка',
                description,
                placement: 'topRight',
            });
        }
        else {
            alert(description);
        }
    }
    return result;
};

/** Найти задачу в черновике getTodoById и применить updater */
const updateTaskInTodoDraft = (
    draft: TodoDetails,
    taskId: number,
    updater: (task: Task) => void,
) => {
    for (const group of draft.groups) {
        const task = group.tasks.find((t) => Number(t.taskId) === Number(taskId));
        if (task) {
            updater(task);
            return;
        }
    }
};

export const tasksSlice = createApi({
    reducerPath: 'tasksApi',

    baseQuery: baseQueryWithErrorHandling,

    endpoints: (builder) => ({
        getTaskActivities: builder.query<Response<Activity[]>, { taskId: number }>({
            query: (request) => ({
                url: '/get-task-activities',
                method: 'POST',
                body: request,
            }),
        }),

        createTask: builder.mutation<Response<{
            taskId: number;
            todoId: number;
            taskName: string;
            taskContent: string;
            uuid: string;
            whoAddTask: string;
            user?: any;
        }>, {
            todoId: number,
            taskName: string,
            taskContent?: string,
        }>({
            query: (request) => ({
                url: '/create-task',
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskName, taskContent = '' }, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;
                    const created = data?.message;
                    if (!created?.taskId) {
                        return;
                    }

                    // Фоллбек: пользователь из auth-кэша, если API не вернул user
                    const authUser = authSlice.endpoints.getUserData.select()(getState() as any)?.data as User | undefined;

                    dispatch(
                        todosSlice.util.updateQueryData(
                            'getTodoById',
                            { todoId: Number(todoId) },
                            (draft) => {
                                const openStatus = 1;
                                const group = draft.groups.find((g) => Number(g.status) === openStatus);
                                if (!group) {
                                    return;
                                }
                                if (group.tasks.some((t) => Number(t.taskId) === Number(created.taskId))) {
                                    return;
                                }
                                group.tasks.unshift({
                                    taskId: Number(created.taskId),
                                    todoId: Number(todoId),
                                    uuid: created.uuid,
                                    tags: [],
                                    status: openStatus,
                                    priority: null,
                                    taskName: created.taskName || taskName,
                                    taskContent: created.taskContent || taskContent || '',
                                    whoAddTask: created.whoAddTask || authUser?.firstName || '',
                                    links: [],
                                    images: [],
                                    comments: [],
                                    user: created.user || authUser,
                                    createDate: new Date().toISOString(),
                                });
                            },
                        ),
                    );
                } catch {
                    // ошибка уже обработана в baseQuery
                }
            },
        }),

        updateTask: builder.mutation<Response, {
            todoId: number,
            taskId: number,
            taskName?: string,
            taskContent?: string,
        }>({
            query: (request) => ({
                url: '/update-task',
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskId, taskName, taskContent }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    todosSlice.util.updateQueryData(
                        'getTodoById',
                        { todoId: Number(todoId) },
                        (draft) => {
                            updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                if (taskName !== undefined) {
                                    task.taskName = taskName;
                                }
                                if (taskContent !== undefined) {
                                    task.taskContent = taskContent;
                                }
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        deleteTask: builder.mutation<Response, { taskId: number }>({
            query: (request) => ({
                url: `/delete-task`,
                method: 'POST',
                body: request,
            }),
        }),

        updateStatus: builder.mutation<Response, { taskId: number, status: number }>({
            query: (request) => ({
                url: `/update-task-status`,
                method: 'POST',
                body: request,
            }),
        }),

        updatePriority: builder.mutation<Response, { todoId: number, taskId: number, priority: number }>({
            query: ({ taskId, priority }) => ({
                url: `/update-task-priority`,
                method: 'POST',
                body: { taskId, priority },
            }),
            async onQueryStarted({ todoId, taskId, priority }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    todosSlice.util.updateQueryData(
                        'getTodoById',
                        { todoId: Number(todoId) },
                        (draft) => {
                            updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                task.priority = priority;
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        addTaskImage: builder.mutation<
            Response<{ imageId: number; imagePath: string }>,
            { todoId: number, taskId: number, file: File }
        >({
            query: (request) => {
                const formData = new FormData();
                formData.append('file', request.file);
                formData.append('taskId', request.taskId.toString());
                formData.append('todoId', request.todoId.toString());

                return {
                    url: `/add-task-image`,
                    method: 'POST',
                    body: formData,
                };
            },
            async onQueryStarted({ todoId, taskId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const image = data?.message;
                    if (!image?.imageId || !image?.imagePath) {
                        return;
                    }

                    dispatch(
                        todosSlice.util.updateQueryData(
                            'getTodoById',
                            { todoId: Number(todoId) },
                            (draft) => {
                                updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                    if (!Array.isArray(task.images)) {
                                        task.images = [];
                                    }
                                    if (!task.images.some((img) => Number(img.imageId) === Number(image.imageId))) {
                                        task.images.push({
                                            imageId: Number(image.imageId),
                                            imagePath: image.imagePath,
                                        });
                                    }
                                });
                            },
                        ),
                    );
                } catch {
                    // ошибка уже обработана в baseQuery
                }
            },
        }),

        deleteTaskImage: builder.mutation<Response, { todoId: number, taskId: number, imageId: number }>({
            query: (request) => ({
                url: `/delete-task-image`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskId, imageId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(
                        todosSlice.util.updateQueryData(
                            'getTodoById',
                            { todoId: Number(todoId) },
                            (draft) => {
                                updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                    if (!Array.isArray(task.images)) {
                                        task.images = [];
                                        return;
                                    }
                                    task.images = task.images.filter(
                                        (img) => Number(img.imageId) !== Number(imageId),
                                    );
                                });
                            },
                        ),
                    );
                } catch {
                    // ошибка уже обработана в baseQuery
                }
            },
        }),

        addTaskLink: builder.mutation<Response<number>, { todoId: number, taskId: number, link: string }>({
            query: (request) => ({
                url: `/add-task-link`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskId, link }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const linkId = data?.message;
                    if (!linkId) {
                        return;
                    }

                    dispatch(
                        todosSlice.util.updateQueryData(
                            'getTodoById',
                            { todoId: Number(todoId) },
                            (draft) => {
                                updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                    if (!Array.isArray(task.links)) {
                                        task.links = [];
                                    }
                                    if (!task.links.some((l) => Number(l.linkId) === Number(linkId))) {
                                        task.links.push({
                                            linkId: Number(linkId),
                                            linkPath: link,
                                        });
                                    }
                                });
                            },
                        ),
                    );
                } catch {
                    // ошибка уже обработана в baseQuery
                }
            },
        }),

        deleteTaskLink: builder.mutation<Response, { todoId: number, taskId: number, linkId: number }>({
            query: (request) => ({
                url: `/delete-task-link`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskId, linkId }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    todosSlice.util.updateQueryData(
                        'getTodoById',
                        { todoId: Number(todoId) },
                        (draft) => {
                            updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                if (!Array.isArray(task.links)) {
                                    task.links = [];
                                    return;
                                }
                                task.links = task.links.filter(
                                    (l) => Number(l.linkId) !== Number(linkId),
                                );
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        addTaskComment: builder.mutation<
            Response<{
                commentId: number;
                comment: string;
                createDate?: string;
                updatedAt?: string | null;
                images?: Array<{ imageId: number; imagePath: string }>;
                user: User;
            }>,
            { todoId: number; taskId: number; comment: string; files?: File[] }
        >({
            query: ({ todoId, taskId, comment, files }) => {
                if (files && files.length > 0) {
                    const formData = new FormData();
                    formData.append('todoId', String(todoId));
                    formData.append('taskId', String(taskId));
                    formData.append('comment', comment || '');
                    files.forEach((file) => formData.append('files', file));
                    return {
                        url: `/add-task-comment`,
                        method: 'POST',
                        body: formData,
                    };
                }

                return {
                    url: `/add-task-comment`,
                    method: 'POST',
                    body: { todoId, taskId, comment },
                };
            },
            async onQueryStarted({ todoId, taskId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const created = data?.message;
                    if (!created?.commentId) {
                        return;
                    }

                    dispatch(
                        todosSlice.util.updateQueryData(
                            'getTodoById',
                            { todoId: Number(todoId) },
                            (draft) => {
                                updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                    if (!Array.isArray(task.comments)) {
                                        task.comments = [];
                                    }
                                    if (!task.comments.some((c) => Number(c.commentId) === Number(created.commentId))) {
                                        task.comments.push({
                                            commentId: Number(created.commentId),
                                            comment: created.comment,
                                            createDate: created.createDate,
                                            updatedAt: created.updatedAt,
                                            images: created.images || [],
                                            user: created.user,
                                        });
                                    }
                                });
                            },
                        ),
                    );
                } catch {
                    // ошибка уже обработана в baseQuery
                }
            },
        }),

        updateTaskComment: builder.mutation<
            Response<{
                commentId: number;
                comment: string;
                createDate?: string;
                updatedAt?: string | null;
                user: User;
            }>,
            { todoId: number; taskId: number; commentId: number; comment: string }
        >({
            query: (request) => ({
                url: `/update-task-comment`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskId, commentId, comment }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    todosSlice.util.updateQueryData(
                        'getTodoById',
                        { todoId: Number(todoId) },
                        (draft) => {
                            updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                const item = task.comments?.find(
                                    (c) => Number(c.commentId) === Number(commentId),
                                );
                                if (item) {
                                    item.comment = comment;
                                    item.updatedAt = new Date().toISOString();
                                }
                            });
                        },
                    ),
                );

                try {
                    const { data } = await queryFulfilled;
                    const updated = data?.message;
                    if (!updated?.commentId) {
                        return;
                    }
                    dispatch(
                        todosSlice.util.updateQueryData(
                            'getTodoById',
                            { todoId: Number(todoId) },
                            (draft) => {
                                updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                    const item = task.comments?.find(
                                        (c) => Number(c.commentId) === Number(updated.commentId),
                                    );
                                    if (item) {
                                        item.comment = updated.comment;
                                        item.updatedAt = updated.updatedAt;
                                    }
                                });
                            },
                        ),
                    );
                } catch {
                    patchResult.undo();
                }
            },
        }),

        deleteTaskComment: builder.mutation<
            Response,
            { todoId: number; taskId: number; commentId: number }
        >({
            query: (request) => ({
                url: `/delete-task-comment`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskId, commentId }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    todosSlice.util.updateQueryData(
                        'getTodoById',
                        { todoId: Number(todoId) },
                        (draft) => {
                            updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                if (!Array.isArray(task.comments)) {
                                    task.comments = [];
                                    return;
                                }
                                task.comments = task.comments.filter(
                                    (c) => Number(c.commentId) !== Number(commentId),
                                );
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        addTaskCommentImage: builder.mutation<
            Response<{ imageId: number; imagePath: string }>,
            { todoId: number; taskId: number; commentId: number; file: File }
        >({
            query: ({ todoId, taskId, commentId, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('todoId', String(todoId));
                formData.append('taskId', String(taskId));
                formData.append('commentId', String(commentId));
                return {
                    url: `/add-task-comment-image`,
                    method: 'POST',
                    body: formData,
                };
            },
            async onQueryStarted({ todoId, taskId, commentId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const image = data?.message;
                    if (!image?.imageId) {
                        return;
                    }
                    dispatch(
                        todosSlice.util.updateQueryData(
                            'getTodoById',
                            { todoId: Number(todoId) },
                            (draft) => {
                                updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                    const item = task.comments?.find(
                                        (c) => Number(c.commentId) === Number(commentId),
                                    );
                                    if (!item) {
                                        return;
                                    }
                                    if (!Array.isArray(item.images)) {
                                        item.images = [];
                                    }
                                    if (!item.images.some((img) => Number(img.imageId) === Number(image.imageId))) {
                                        item.images.push({
                                            imageId: Number(image.imageId),
                                            imagePath: image.imagePath,
                                        });
                                    }
                                });
                            },
                        ),
                    );
                } catch {
                    // toast
                }
            },
        }),

        deleteTaskCommentImage: builder.mutation<
            Response,
            { todoId: number; taskId: number; commentId: number; imageId: number }
        >({
            query: (request) => ({
                url: `/delete-task-comment-image`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, taskId, commentId, imageId }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    todosSlice.util.updateQueryData(
                        'getTodoById',
                        { todoId: Number(todoId) },
                        (draft) => {
                            updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                const item = task.comments?.find(
                                    (c) => Number(c.commentId) === Number(commentId),
                                );
                                if (item?.images) {
                                    item.images = item.images.filter(
                                        (img) => Number(img.imageId) !== Number(imageId),
                                    );
                                }
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        updateTags: builder.mutation<Response, { todoId: number, taskId: number, tags: string[] }>({
            query: ({ taskId, tags }) => ({
                url: `/update-task-tags`,
                method: 'POST',
                body: { taskId, tags },
            }),
            async onQueryStarted({ todoId, taskId, tags }, { dispatch, queryFulfilled }) {
                // Сразу обновляем карточку, не дожидаясь MQTT
                const patchResult = dispatch(
                    todosSlice.util.updateQueryData(
                        'getTodoById',
                        { todoId: Number(todoId) },
                        (draft) => {
                            updateTaskInTodoDraft(draft, Number(taskId), (task) => {
                                task.tags = tags;
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    }),
});

export const {
    useGetTaskActivitiesQuery,
    useCreateTaskMutation,
    useUpdateTaskMutation,
    useDeleteTaskMutation,
    useUpdateStatusMutation,
    useUpdatePriorityMutation,
    useAddTaskImageMutation,
    useDeleteTaskImageMutation,
    useAddTaskLinkMutation,
    useDeleteTaskLinkMutation,
    useAddTaskCommentMutation,
    useUpdateTaskCommentMutation,
    useDeleteTaskCommentMutation,
    useAddTaskCommentImageMutation,
    useDeleteTaskCommentImageMutation,
    useUpdateTagsMutation,
} = tasksSlice;
